import React, { useState, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import { LuClock } from "react-icons/lu";
import { IoMdArrowDropdown } from "react-icons/io";
import { toast, ToastContainer } from "react-toastify";
import { supabase } from "../supabase";

function PlanYourTripModal({
  show,
  onClose,
  onSearchRide,
  initialDrop,
  initialPickup,
  loading,
  initialScheduled,
  initialPickupDate,
  initialPickupTime,
}) {
  const airportName = "Dibrugarh Airport (DIB)";

  const [pickup, setPickup] = useState("");
  const [drop, setDrop] = useState("");
  const [isScheduled, setIsScheduled] = useState(false);
  const [pickupDate, setPickupDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [showDateTime, setShowDateTime] = useState(false);
  const [showPickupMenu, setShowPickupMenu] = useState(false);
  const [activeField, setActiveField] = useState(null);

  const [isValidDrop, setIsValidDrop] = useState(false);
  const [isValidPickup, setIsValidPickup] = useState(true);

  // ✅ Rates from Supabase
  const [rates, setRates] = useState([]);
  const [filteredRates, setFilteredRates] = useState([]);

  // ===============================
  // Initialize values when modal opens
  // ===============================
  useEffect(() => {
    if (show) {
      setPickup(initialPickup || "Dibrugarh Airport (DIB)");
      setDrop(initialDrop || "");

      const scheduled = initialScheduled === true;

      setIsScheduled(scheduled);
      setPickupDate(initialPickupDate || "");
      setPickupTime(initialPickupTime || "");

      // 👇 Important fix
      setShowDateTime(false);
    }
  }, [
    show,
    initialDrop,
    initialPickup,
    initialScheduled,
    initialPickupDate,
    initialPickupTime,
  ]);

  // ===============================
  // Fetch Rates from Supabase
  // ===============================
  useEffect(() => {
    const fetchRates = async () => {
      const { data, error } = await supabase.from("Rates").select("*");

      if (error) {
        console.error("Error fetching rates:", error);
        return;
      }

      setRates(data || []);
    };

    fetchRates();
  }, []);

  // ===============================
  // Filter Supabase Rates for Drop
  // ===============================
  useEffect(() => {
    if (!activeField) return;

    const delay = setTimeout(() => {
      if (!drop) {
        setFilteredRates([]);
        return;
      }

      const filtered = rates.filter((rate) =>
        rate.place?.toLowerCase().includes(drop.toLowerCase()),
      );

      setFilteredRates(filtered);
    }, 300);

    return () => clearTimeout(delay);
  }, [drop, activeField, rates]);

  useEffect(() => {
    const delay = setTimeout(() => {
      const value = activeField === "pickup" ? pickup : drop;

      if (!value) {
        setFilteredRates([]);
        return;
      }

      const filtered = rates.filter((rate) =>
        rate.place?.toLowerCase().includes(value.toLowerCase()),
      );

      setFilteredRates(filtered);
    }, 300);

    return () => clearTimeout(delay);
  }, [pickup, drop, activeField, rates]);

  // ===============================
  // Default schedule time
  // ===============================
  useEffect(() => {
    if (showDateTime && !pickupTime) {
      const now = new Date();
      now.setMinutes(now.getMinutes() + 15);

      setPickupDate(now.toISOString().split("T")[0]);
      setPickupTime(now.toTimeString().slice(0, 5));
    }
  }, [showDateTime]);

  const handleSearchRide = () => {
    if (!pickup || !drop) {
      toast.error("Please enter pickup and drop locations");
      return;
    }

    if (pickup === drop) {
      toast.error("Pickup and Drop cannot be the same location");
      return;
    }

    if (!isValidPickup || !isValidDrop) {
      toast.error("Please select locations from suggestions only");
      return;
    }

    onSearchRide({
      pickup,
      drop,
      isScheduled,
      pickupDate: isScheduled ? pickupDate : null,
      pickupTime: isScheduled ? pickupTime : null,
    });

    onClose();
  };

  if (!show) return null;

  return (
    <>
   
      <div className="bottom-modal-overlay" onClick={onClose}>
        <div className="bottom-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <h5>Plan your trip</h5>
            <IoClose className="close-icon" onClick={onClose} />
          </div>

          {/* Pickup Now / Later */}
          <div className="d-flex gap-2 my-2 justify-content-between">
            <div className="pickup-dropdown">
              <button
                className="badge-secondary"
                type="button"
                onClick={() => setShowPickupMenu(!showPickupMenu)}
              >
                <LuClock className="icon me-2" />
                {isScheduled ? "Schedule for later" : "Pick-up now"}
                <IoMdArrowDropdown className="icon ms-1" />
              </button>

              {showPickupMenu && (
                <div className="pickup-menu">
                  <button
                    className="pickup-option"
                    onClick={() => {
                      setIsScheduled(false);
                      setPickupDate("");
                      setPickupTime("");
                      setShowDateTime(false);
                      setShowPickupMenu(false);
                    }}
                  >
                    ⚡ Pick-up now
                  </button>

                  <button
                    className="pickup-option"
                    onClick={() => {
                      setIsScheduled(true); // 👈 change state immediately
                      setShowDateTime(true);
                      setShowPickupMenu(false);
                    }}
                  >
                    🕒 Schedule for later
                  </button>
                </div>
              )}
            </div>
          </div>

          {isScheduled && pickupDate && pickupTime && (
            <div className="scheduled-preview mt-3">
              <LuClock className="me-1" />
              <span>
                Scheduled:{" "}
                {new Date(pickupDate).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                })}{" "}
                at{" "}
                {new Date(`1970-01-01T${pickupTime}`).toLocaleTimeString(
                  "en-IN",
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  },
                )}
              </span>

              <button
                className="change-btn"
                onClick={() => setShowDateTime(true)}
              >
                Change
              </button>
            </div>
          )}

          {/* Date Time Selector */}
          {showDateTime && (
            <div className="datetime-box">
              <label className="small fw-bold">Select Pickup Date</label>
              <input
                type="date"
                className="form-control"
                value={pickupDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => setPickupDate(e.target.value)}
              />

              <label className="small fw-bold mt-2">Select Pickup Time</label>
              <input
                type="time"
                className="form-control"
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
              />

              <button
                className="modal-btn mt-2 confirm-btn"
                onClick={() => {
                  if (!pickupDate || !pickupTime) {
                    toast.error("Select date & time");
                    return;
                  }

                  setIsScheduled(true);
                  setShowDateTime(false);
                }}
              >
                Confirm Schedule
              </button>
            </div>
          )}

          {/* Pickup Input */}
          <div className="input-wrapper">
            <input
              type="text"
              className="modal-input"
              placeholder="Enter pickup location"
              value={pickup}
              onFocus={() => setActiveField("pickup")}
              onChange={(e) => {
                setPickup(e.target.value);
                setIsValidPickup(false);
              }}
            />

            {pickup && (
              <button
                className="clear-btn"
                style={{ marginTop: "5px" }}
                onClick={() => {
                  setPickup("");
                  setIsValidPickup(false);
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Airport Suggestion */}
          {activeField === "pickup" && (
            <div className="suggestion">
              {/* Airport Option */}
              <div
                className="suggestion-item highlight-airport"
                onClick={() => {
                  if (airportName === drop) {
                    toast.error("Pickup and Drop cannot be the same location");
                    return;
                  }

                  setPickup(airportName);
                  setActiveField(null);
                  setIsValidPickup(true);
                }}
              >
                <strong>✈ {airportName}</strong>
                <div className="small text-muted">Dibrugarh, Assam</div>
              </div>

              {/* Supabase Suggestions */}
              {filteredRates.map((rate, index) => (
                <div
                  key={index}
                   className="suggestion-item my-1"
                  onClick={() => {
                    if (rate.place === drop) {
                      toast.error(
                        "Pickup and Drop cannot be the same location",
                      );
                      return;
                    }

                    setPickup(rate.place);
                    setFilteredRates([]);
                    setActiveField(null);
                    setIsValidPickup(true);
                  }}
                >
                  {rate.place}
                </div>
              ))}
            </div>
          )}

          {/* Drop Input */}
          <div className="input-wrapper">
            <input
              type="text"
              placeholder="Enter drop location"
              className="modal-input"
              value={drop}
              onFocus={() => setActiveField("drop")}
              onChange={(e) => {
                setDrop(e.target.value);
                setIsValidDrop(false); // reset validity when typing
              }}
            />
            {drop && (
              <button
                className="clear-btn"
                style={{ marginTop: "5px" }}
                onClick={() => {
                  setDrop("");
                  setIsValidDrop(false);
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Supabase Drop Suggestions */}
          {activeField === "drop" && (
            <div className="suggestion">
              <div
                className="suggestion-item highlight-airport"
                onClick={() => {
                  if (airportName === pickup) {
                    toast.error("Pickup and Drop cannot be the same location");
                    return;
                  }

                  setDrop(airportName);
                  setActiveField(null);
                  setIsValidDrop(true);
                }}
              >
                <strong>✈ {airportName}</strong>
                <div className="small text-muted">Dibrugarh, Assam</div>
              </div>
              {filteredRates.map((rate, index) => (
                <div
                  key={index}
                  className="suggestion-item my-1"
                  // onClick={() => {
                  //   setDrop(rate.place);
                  //   setFilteredRates([]);
                  //   setActiveField(null);
                  //   setIsValidDrop(true); // ✅ mark as valid
                  // }}
                  onClick={() => {
                    if (rate.place === pickup) {
                      toast.error(
                        "Pickup and Drop cannot be the same location",
                      );
                      return;
                    }

                    setDrop(rate.place);
                    setFilteredRates([]);
                    setActiveField(null);
                    setIsValidDrop(true);
                  }}
                >
                  {rate.place}
                </div>
              ))}
            </div>
          )}

          {/* Search Button */}
          <button
            className="btn btn-dark py-3 w-100 mt-3"
            onClick={handleSearchRide}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm"></span>
                Updating...
              </>
            ) : (
              "Search My Ride"
            )}
          </button>
        </div>
      </div>
    </>
  );
}

export default PlanYourTripModal;
